import PropTypes from 'prop-types';

function formatNumber(value, suffix = '') {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const formatted = Math.abs(value) >= 1 ? value.toFixed(1) : value.toFixed(2);
  return `${formatted}${suffix}`;
}

export default function EngineSummaryCard({
  engine,
  stats = {},
  trades = [],
  blockedReason = null,
  openPosition = null,
}) {
  const wins = stats.wins || 0;
  const losses = stats.losses || 0;
  const flats = stats.flats || 0;
  const total = stats.count || trades.length || 0;
  const avgPnl = stats.avg_pnl ?? 0;
  const statusChip = blockedReason
    ? { text: blockedReason, color: 'bg-red-900/50 text-red-200 border-red-700/60' }
    : { text: 'Raw telemetry', color: 'bg-gray-900/60 text-gray-200 border-gray-700/60' };

  const lastTrade = trades[0];

  return (
    <div
      className="rounded-2xl border border-gray-800 bg-gray-900/70 p-4 space-y-3 shadow"
      data-testid={`${engine}-summary-card`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">Engine</p>
          <h3 className="text-lg font-semibold text-white">{engine}</h3>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full border ${statusChip.color}`}>{statusChip.text}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm text-gray-200">
        <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
          <div className="text-gray-500 text-[0.7rem] uppercase">Trades (24h)</div>
          <div className="text-xl font-semibold text-cyan-200">{total}</div>
          <div className="text-xs text-gray-400">Wins {wins} / Losses {losses} / Flat {flats}</div>
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
          <div className="text-gray-500 text-[0.7rem] uppercase">Raw Avg PnL</div>
          <div className={`text-xl font-semibold ${avgPnl >= 0 ? 'text-green-200' : 'text-red-200'}`}>
            {formatNumber(avgPnl, '%')}
          </div>
          <div className="text-xs text-amber-100">Raw telemetry; not clean measurement evidence.</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs text-gray-300">
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3 space-y-1">
          <div className="text-gray-500 text-[0.65rem] uppercase">Open Position</div>
          {openPosition ? (
            <>
              <div className="font-semibold text-white">{openPosition.symbol}</div>
              <div className="text-gray-400">Entry: {formatNumber(openPosition.entry_price)}</div>
            </>
          ) : (
            <div className="text-gray-500">None</div>
          )}
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3 space-y-1">
          <div className="text-gray-500 text-[0.65rem] uppercase">Last Trade</div>
          {lastTrade ? (
            <>
              <div className="font-semibold text-white">{lastTrade.coin} · {lastTrade.side}</div>
              <div className="text-gray-400">Confidence {formatNumber(lastTrade.confidence, '')}</div>
            </>
          ) : (
            <div className="text-gray-500">Waiting for activity</div>
          )}
        </div>
      </div>
    </div>
  );
}

EngineSummaryCard.propTypes = {
  engine: PropTypes.string.isRequired,
  stats: PropTypes.object,
  trades: PropTypes.arrayOf(PropTypes.object),
  blockedReason: PropTypes.string,
  openPosition: PropTypes.object,
};
